import { ApiClient } from '@/shared/api-client/api-client';
import { IPagedList } from '@/shared/api-client/models';
import { IMyMotionsListEntry } from '@/shared/api-client/models/motion/my-motions-list-entry';
import { friendlyDateTime } from '@/shared/filters/date-time-filter';
import { ArgumentValidator } from '@/shared/helpers/argument-validator';
import { StringComparer } from '@/shared/helpers/string-comparer';
import { UIEventHandler } from '@/shared/helpers/ui-event-handler';
import { IPagination } from '@/shared/Models/pagination';
import { Component, Prop, Vue } from 'vue-property-decorator';
import MyMotionsStatusViewerComponent from './my-motions-status-viewer-component/my-motions-status-viewer-component.vue';

interface IViewModel {
    pagedItems: IPagedList<IMyMotionsListEntry>;
    pagination: IPagination;
    isInitialized: boolean;
    isLoading: boolean;
}

@Component({
    computed: {
        StringComparer() {
            return StringComparer
        }
    },
    components: {
        MyMotionsStatusViewerComponent
    },
    filters: {
        friendlyDateTime
    }
})
export default class MyMotionsComponent extends Vue {

    @Prop() public translations!: { [key: string]: string; };

    public async created() {
        await this.refreshList();
    }

    private apiClient: ApiClient = new ApiClient();

    public viewModel: IViewModel = {
        pagedItems: {
            items: [],
            totalNbrOfItems: 0
        },
        pagination: {
            page: 1,
            pageSize: 15,
            total: -1
        },
        isInitialized: false,
        isLoading: false,
    }

    public get anyMotionCanBeResubmitted(): boolean {

        if (!this.viewModel?.pagedItems?.items) {
            return false;
        }

        return this.viewModel.pagedItems.items.some(x => x.canBeResubmitted);
    }

    public printProperty(motion: IMyMotionsListEntry, propertyIdentifier: string, defaultValue = 'N/A'): string {

        ArgumentValidator.notNullOrEmpty(motion, `motion`);
        ArgumentValidator.notNullOrEmpty(propertyIdentifier, `propertyName`);

        return motion.properties.find(prop => prop.identifier === propertyIdentifier)?.value || defaultValue;
    }

    public async pageChanged(page: number): Promise<void> {

        this.viewModel.pagination.page = page;

        await this.refreshList();
    }

    public async resubmit(motion: IMyMotionsListEntry, $event: UIEvent): Promise<void> {

        UIEventHandler.preventDefaults($event);

        if (!confirm(this.translations['general.confirm'])) {
            return;
        }

        await this.apiClient.motion.resubmit(motion.activityId, motion.id);

        this.refreshList();
    }

    public async deleteMotion(motion: IMyMotionsListEntry, $event: UIEvent): Promise<void> {

        UIEventHandler.preventDefaults($event);

        if (!confirm(this.translations['general.confirm'])) {
            return;
        }

        await this.apiClient.motion.delete(motion.activityId, motion.id);

        this.refreshList();
    }

    private async refreshList(): Promise<void> {

        try {

            this.viewModel.isLoading = true;

            this.viewModel.pagedItems = await this.apiClient.motion.listMyMotions(
                this.viewModel.pagination
            );

            this.viewModel.pagination.total = this.viewModel.pagedItems.totalNbrOfItems;

            this.viewModel.isInitialized = true;

        } finally {

            this.viewModel.isLoading = false;
        }
    }
}